import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { ACCOUNT_SLUG_MAX_LENGTH, AccountSlugProblem, AccountSlugRefusal } from 'fym-shared/src/music24/accountSlug';
import { TypedMemo } from '../../common/types';
import { AddressState } from '../../smartLinks/profile/useAccountSlugCheck';
import { WelcomeSaveError } from './welcomeSaveError';
import { welcomeMessages as messages } from './welcomeMessages';
import { AddressStatus, AddressStatusKind } from './AddressStatus';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole welcome page; each part uses its own slice of it */
import styles from './WelcomeView.module.scss';
/* eslint-enable css-modules/no-unused-class */

const ADDRESS_INPUT_ID = 'welcome-artist-address';

interface ArtistAddressPreviewProps {
  slug: string;
  setSlug: (value: string) => void;
  /** The platform domain of the artist, from `resolveSmartLinkPlatformDomain`. */
  domain: string;
  slugState: AddressState;
  /** The problem that the address rules find before the server (`accountSlugProblem`). */
  slugProblem?: AccountSlugProblem | null;
  /** The exact reason why the address cannot be used (QA brk-r2-4). */
  slugRefusal?: AccountSlugRefusal | null;
  suggestion?: string;
  error: WelcomeSaveError | null;
  disabled: boolean;
}

/**
 * The save error wins over the live check: the server has the last word (design: onboarding a6).
 * The address rules come next: they need no server.
 */
export function statusKind(
  slugState: AddressState,
  error: WelcomeSaveError | null,
  slugProblem: AccountSlugProblem | null = null,
  slugRefusal: AccountSlugRefusal | null = null
): AddressStatusKind {
  if (error === 'taken') {
    return AddressState.taken;
  }
  if (error === 'reserved' || slugProblem === 'reserved' || slugRefusal === 'reserved') {
    return 'reserved';
  }
  if (error === 'invalid' || slugProblem === 'invalid') {
    return AddressState.illegal;
  }
  return slugState;
}

/**
 * The SmartLink address `<slug>.<domain>` under the name (design: onboarding a1, a6, a8).
 * "Edit address" turns the line into a field. A bad address keeps the field open.
 */
const ArtistAddressPreviewComponent: React.FC<ArtistAddressPreviewProps> = ({
  slug,
  setSlug,
  domain,
  slugState,
  slugProblem = null,
  slugRefusal = null,
  suggestion,
  error,
  disabled,
}) => {
  const intl = useIntl();
  const [isEditing, setIsEditing] = useState(false);
  const kind = statusKind(slugState, error, slugProblem, slugRefusal);
  const isBad = kind === AddressState.taken || kind === AddressState.illegal || kind === 'reserved';
  const showField = isEditing || isBad;

  // A bad address opens the field, and the field stays open after the fix.
  useEffect(() => {
    if (isBad) {
      setIsEditing(true);
    }
  }, [isBad]);

  return (
    <div className={styles.field}>
      {showField ? (
        <>
          <label className={styles.label} htmlFor={ADDRESS_INPUT_ID}>
            {intl.formatMessage(messages.addressLabel)}
          </label>
          <div className={clsx(styles.input, styles.inputMono, isBad && styles.inputBad)}>
            <input
              id={ADDRESS_INPUT_ID}
              value={slug}
              onChange={(event) => setSlug(event.target.value)}
              maxLength={ACCOUNT_SLUG_MAX_LENGTH}
              autoComplete="off"
              spellCheck={false}
              disabled={disabled}
              aria-invalid={isBad}
            />
            <span className={styles.suffix}>.{domain}</span>
          </div>
          <AddressStatus kind={kind} refusal={slugRefusal} suggestion={suggestion} onUseSuggestion={setSlug} />
        </>
      ) : (
        <div className={styles.addr} data-testid="welcome-address">
          <span className={styles.url} title={`${slug}.${domain}`}>
            <b>{slug}</b>.{domain}
          </span>
          <AddressStatus kind={kind} suggestion={suggestion} onUseSuggestion={setSlug} chipOnly />
          <button type="button" className={styles.link} onClick={() => setIsEditing(true)} disabled={disabled}>
            {intl.formatMessage(messages.addressEdit)}
          </button>
        </div>
      )}
      <p className={styles.hint}>{intl.formatMessage(messages.addressHint)}</p>
    </div>
  );
};

export const ArtistAddressPreview = TypedMemo(ArtistAddressPreviewComponent);
